import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import {
  Users, Plus, Search, Phone, Mail, Building2, Star, Target, ArrowRight, Upload,
  Handshake, ChevronLeft, ChevronRight, Package, Sparkles, CheckCircle2,
  AlertTriangle, TrendingUp, SlidersHorizontal
} from "lucide-react";
// Iter 167.g — ContactDetailPanel import removed; full-page nav replaces lightbox.
import ContactFilterPanel, { EMPTY_CONTACT_FILTERS, countActiveContactFilters, applyContactFilters } from "@/components/ContactFilterPanel";
import LeadFilterPanel, { applyLeadFilters } from "@/components/LeadFilterPanel";
import { applyLocationFilters, applyLastContactFilter } from "@/lib/leadFilterConfig";
import { useGlobalLeadFilters, clearGlobalLeadFilters } from "@/lib/globalLeadFilters";
import CSVImportModal from "@/components/CSVImportModal";

const TABS = [
  // Iter 167.e (Danny — Customers Section Overhaul) — renamed user-facing
  // labels. Keys stay the same so existing filter state in localStorage
  // continues to work.
  { key: "all", label: "All customers" },
  { key: "live_client", label: "Live customers" },
  { key: "active", label: "Active leads" },
  { key: "referral", label: "Referral sources" },
];

const ALL_PRODUCTS = [
  "AI-powered EHR (MDNet)", "Medical Billing", "RevQ AI Billing",
  "Practice Management", "CCM / APCM / RPM", "REACH", "AWV Program",
  "MIPS Advisory", "Telehealth", "Patient Portal"
];

const PAGE_SIZE = 10;

export default function Contacts() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [tab, setTab] = useState("all");
  // Iter 167.e (Danny — Customers Section) — Live/Implementation bucket
  // filter pill. Defaults to "all". Persisted to localStorage so the
  // user's last view sticks across reloads.
  const [bucket, setBucketRaw] = useState(() => {
    try { return localStorage.getItem("customers_bucket") || "all"; } catch (_) { return "all"; }
  });
  const setBucket = (v) => {
    setBucketRaw(v);
    try { localStorage.setItem("customers_bucket", v); } catch (_) {}
  };
  // Iter 167.f — Onboarding Specialist focus filter (spec Part 6).
  // When ON, only show customers where the current user is the assigned
  // Onboarding Specialist (`implementation.specialist_user_id == me`).
  // Auto-enabled if the user's role is `onboarding_specialist`.
  const [myImplOnly, setMyImplOnly] = useState(() => {
    try {
      const role = (JSON.parse(localStorage.getItem("user") || "null")?.role || "").toLowerCase();
      const persisted = localStorage.getItem("customers_my_impl");
      return persisted != null ? persisted === "1" : role === "onboarding_specialist";
    } catch (_) { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("customers_my_impl", myImplOnly ? "1" : "0"); } catch (_) {}
  }, [myImplOnly]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  // Iter 167.g — panelContact + handleContactUpdate kept as no-ops to
  // avoid touching downstream code that might still reference them.
  // The slide-out panel itself is no longer rendered.
  const [panelContact, setPanelContact] = useState(null);
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [showLeadFilters, setShowLeadFilters] = useState(false);
  const [cFilters, setCFilters] = useState({ ...EMPTY_CONTACT_FILTERS });

  // Iter 138 ioannis@ehiehr.com fix: same lead-filter sidebar that lives
  // on /leads now also drives filtering on /contacts via the shared
  // localStorage store. Contacts get filtered through their linked lead
  // (by contact_id, or fallback email match).
  const [leadFilters, setLeadFilters, leadExtra, setLeadExtra, leadFilterCount] = useGlobalLeadFilters();

  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchData defined below; run once on mount
  useEffect(() => { fetchData(); }, []);
  useEffect(() => { setPage(1); }, [tab, search, cFilters, leadFilters, leadExtra]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [contactsRes, leadsRes] = await Promise.all([
        axios.get(`${API}/contacts`, { headers }),
        axios.get(`${API}/leads`, { headers }),
      ]);
      setContacts(contactsRes.data || []);
      setLeads(leadsRes.data || []);
    } catch {}
    setLoading(false);
  };

  const getContactProducts = (contact) => {
    const cLeads = leads.filter(l =>
      l.contact_id === contact.id || (l.email && l.email === contact.email)
    );
    return [...new Set(cLeads.flatMap(l => l.product_interest || []))];
  };

  const getMissingProducts = (contact) => {
    const current = getContactProducts(contact);
    return ALL_PRODUCTS.filter(p => !current.some(cp => cp.includes(p) || p.includes(cp)));
  };

  const filtered = (() => {
    let result = contacts.filter(c => {
      const matchTab = tab === "all" || c.status === tab;
      const q = search.toLowerCase();
      const matchSearch = !q || [c.first_name, c.last_name, c.company, c.email].some(v => (v || "").toLowerCase().includes(q));
      return matchTab && matchSearch;
    });
    const cFilterCount = countActiveContactFilters(cFilters);
    if (cFilterCount > 0) result = applyContactFilters(result, leads, cFilters);

    // Iter 138 — apply the shared Leads filter sidebar to contacts by
    // looking up each contact's linked lead and running the same
    // applyLeadFilters / location / last-contact filter chain that
    // /leads itself uses. A contact with no matching lead is excluded
    // only when at least one lead filter is active.
    if (leadFilterCount > 0) {
      const byContactId = new Map();
      const byEmail = new Map();
      leads.forEach((l) => {
        if (l.contact_id) byContactId.set(l.contact_id, l);
        if (l.email) byEmail.set(String(l.email).toLowerCase(), l);
      });
      // Iter 159 (Josh — Lisa blank /contacts 2026-06-03): chain the three
      // filter helpers correctly. `applyLeadFilters` takes the array;
      // `applyLocationFilters` + `applyLastContactFilter` each take a
      // SINGLE lead and return a boolean — they're predicates for .filter()
      // not array transforms. The old chain passed an array to a single-
      // lead predicate, which returned `true` (truthy non-array), and the
      // subsequent `.map((l) => l.id)` threw "TypeError: .map is not a
      // function", blanking the whole page for anyone with a saved lead
      // filter in localStorage.
      const matchedLeads = new Set(
        applyLeadFilters(leads, leadFilters)
          .filter((l) => applyLocationFilters(l, leadExtra))
          .filter((l) => applyLastContactFilter(l, leadExtra.lastContact))
          .map((l) => l.id),
      );
      result = result.filter((c) => {
        const linked = byContactId.get(c.id) || byEmail.get(String(c.email || "").toLowerCase());
        return linked && matchedLeads.has(linked.id);
      });
    }
    return result;
  })();

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  // (paged kept for back-compat; bucket-aware view uses `pagedFinal` below.)

  // Iter 167.e — derive Live vs. Implementation bucket per spec Part 3.
  // Matches backend `_derive_bucket` so client + server agree on counts.
  // Iter 167.g — explicit `bucket` field on the doc takes precedence
  // over derivation (so brand-new customers tagged "implementation"
  // via the AddLead form don't get misclassified as "live").
  const deriveBucket = (c) => {
    const explicit = (c?.bucket || "").toLowerCase().trim();
    if (explicit === "live" || explicit === "implementation" || explicit === "churned_exited") return explicit;
    // Iter 171.A — exit payload (without explicit bucket) still routes to churned.
    if (c?.exit?.sub_type) return "churned_exited";
    const ep = Array.isArray(c?.enrolled_products) ? c.enrolled_products : [];
    if (ep.length === 0) return "live";
    const statuses = new Set(ep.map(p => (p?.status || "").toLowerCase()));
    if (statuses.size > 0 && [...statuses].every(s => s === "live")) return "live";
    return "implementation";
  };
  const liveBucketCount = contacts.filter(c => deriveBucket(c) === "live").length;
  const implementationBucketCount = contacts.filter(c => deriveBucket(c) === "implementation").length;
  const churnedBucketCount = contacts.filter(c => deriveBucket(c) === "churned_exited").length;
  // Apply the bucket filter on top of `filtered`. We do this AFTER the
  // existing tab/search/lead-filter chain so it composes naturally
  // (e.g. tab=active leads + bucket=implementation works as expected).
  let bucketFiltered = bucket === "all"
    ? filtered
    : filtered.filter(c => deriveBucket(c) === bucket);
  // Iter 167.f — Onboarding Specialist focus: when toggled on, restrict
  // to customers where I'm the assigned specialist. Forces bucket=
  // implementation since live customers don't have active specialists.
  const currentUserId = (() => {
    try { return JSON.parse(localStorage.getItem("user") || "null")?.id || null; }
    catch (_) { return null; }
  })();
  if (myImplOnly && currentUserId) {
    bucketFiltered = bucketFiltered.filter(c => (c?.implementation?.specialist_user_id || null) === currentUserId);
  }
  const pagedFinal = bucketFiltered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const bucketTotalPages = Math.max(1, Math.ceil(bucketFiltered.length / PAGE_SIZE));

  const liveCount = contacts.filter(c => c.status === "live_client").length;
  const activeCount = contacts.filter(c => c.status === "active").length;
  const referralCount = contacts.filter(c => c.status === "referral").length;

  // Iter 167.g (Danny) — clicking a customer now opens the full Customer
  // Focus page at /customers/{id} instead of the slide-out lightbox.
  // The lightbox is removed entirely so the user only ever sees one
  // detail surface, mirroring how Leads work.
  const openPanel = useCallback((contact) => {
    if (contact?.id) navigate(`/customers/${contact.id}`);
  }, [navigate]);

  const handleContactUpdate = (id, updates) => {
    setContacts(prev => prev.map(c => c.id === id ? { ...c, ...updates } : c));
    if (panelContact?.id === id) setPanelContact(prev => ({ ...prev, ...updates }));
  };

  const isLiveClientTab = tab === "live_client";

  const [csvOpen, setCsvOpen] = useState(false);

  return (
    <div data-testid="customers-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Customers</h1>
          <p className="page-subtitle">{contacts.length} total customers</p>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button className="btn" onClick={() => setCsvOpen(true)} data-testid="csv-import-contacts-btn"><Upload style={{ width: 13, height: 13 }} /> Import CSV</button>
          {/* Iter 177.8 (Tatiana #32edebaa Feb 2026) — "+ Add Customer
              button does nothing" report. The plain `navigate(...)`
              click handler was silently no-op'ing for at least one
              user; root cause unconfirmed but most likely an event
              propagation / focus interaction. Switched to an anchor
              with an explicit click handler that ALSO calls
              navigate() — keeps SPA routing for the common case but
              gives the browser a real link to follow if React is
              wedged for any reason. Also logs the click + adds a
              defensive toast on navigation failure so we'll see WHY
              if the bug still repros. */}
          <a
            href="/add-lead"
            className="btn btn-primary"
            data-testid="add-customer-btn"
            style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }}
            onClick={(e) => {
              e.preventDefault();
              try {
                // eslint-disable-next-line no-console
                console.debug("[customers] add-customer click → /add-lead");
                navigate("/add-lead");
              } catch (err) {
                // eslint-disable-next-line no-console
                console.error("[customers] navigate('/add-lead') threw:", err);
                // Hard fallback so the user is never stuck — the
                // anchor href will take them there even if SPA
                // navigation has crashed.
                window.location.href = "/add-lead";
              }
            }}
          >
            <Plus style={{ width: 13, height: 13 }} /> Add Customer
          </a>
        </div>
      </div>

      <div className="page-content">
        {/* Stats */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 14 }}>
          {[
            { label: "Total customers", value: contacts.length, icon: Users, bg: "var(--blue-light)", color: "var(--blue)" },
            { label: "Live customers", value: liveCount, icon: Handshake, bg: "var(--green-light)", color: "var(--green)" },
            { label: "Active leads", value: activeCount, icon: Target, bg: "var(--amber-light)", color: "var(--amber)" },
            { label: "Referral sources", value: referralCount, icon: Star, bg: "#f8f5ff", color: "var(--purple)" },
          ].map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="card" style={{ padding: "14px 16px", display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: s.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Icon style={{ width: 16, height: 16, color: s.color }} />
                </div>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 700 }}>{s.value}</div>
                  <div style={{ fontSize: 11, color: "var(--text-tertiary)" }}>{s.label}</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Tabs + Search */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <div style={{ display: "flex", gap: 2 }}>
            {TABS.map(t => (
              <button key={t.key} className={`btn ${tab === t.key ? "btn-primary" : ""}`} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
                style={{ fontSize: 11, padding: "5px 12px" }}>
                {t.label}
              </button>
            ))}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <button className="btn" onClick={() => setShowLeadFilters(true)} data-testid="open-lead-filters-btn"
              style={leadFilterCount > 0 ? { background: "var(--blue-light)", color: "var(--blue)", borderColor: "var(--blue)", fontSize: 11, padding: "5px 10px" } : { fontSize: 11, padding: "5px 10px" }}
              title="Apply the same Leads filter sidebar to these contacts (filters carry over from the Leads page)">
              <SlidersHorizontal style={{ width: 12, height: 12 }} /> Lead filters {leadFilterCount > 0 && <span className="tag tag-blue" style={{ fontSize: 9, marginLeft: 3 }}>{leadFilterCount}</span>}
            </button>
            <button className="btn" onClick={() => setShowFilters(true)} data-testid="open-contact-filters-btn"
              style={countActiveContactFilters(cFilters) > 0 ? { background: "var(--blue-light)", color: "var(--blue)", borderColor: "var(--blue)", fontSize: 11, padding: "5px 10px" } : { fontSize: 11, padding: "5px 10px" }}>
              <SlidersHorizontal style={{ width: 12, height: 12 }} /> Contact filters {countActiveContactFilters(cFilters) > 0 && <span className="tag tag-blue" style={{ fontSize: 9, marginLeft: 3 }}>{countActiveContactFilters(cFilters)}</span>}
            </button>
            <div style={{ display: "flex", alignItems: "center", gap: 6, border: "0.5px solid var(--border)", borderRadius: 8, padding: "5px 10px", background: "var(--surface)" }}>
              <Search style={{ width: 13, height: 13, color: "var(--text-tertiary)" }} />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search customers..."
                data-testid="customers-search"
                style={{ border: "none", outline: "none", fontSize: 12, background: "transparent", width: 160 }} />
            </div>
          </div>
        </div>

        {/* Iter 167.e — Live / Implementation bucket pill toggle (spec Part 3).
            Uses the new /api/customers?bucket=... endpoint. The "All
            customers" pill is the default and applies no bucket filter.
            Counts come from the in-memory rows (already on the client). */}
        <div data-testid="customer-bucket-pills" style={{ display: "flex", gap: 6, marginBottom: 12 }}>
          {[
            { key: "all", label: "All customers", count: contacts.length, bg: "var(--surface)", color: "var(--text-primary)" },
            { key: "live", label: "Live", count: liveBucketCount, bg: "var(--green-light)", color: "var(--green)" },
            { key: "implementation", label: "Implementation", count: implementationBucketCount, bg: "var(--blue-light)", color: "var(--blue)" },
            // Iter 171.A — Churned / Exited bucket pill (collapsed-by-default
            // per spec — admins must actively select it to see churned rows).
            { key: "churned_exited", label: "Churned / Exited", count: churnedBucketCount, bg: "#F1F5F9", color: "#475569" },
          ].map(b => (
            <button
              key={b.key}
              data-testid={`bucket-pill-${b.key}`}
              onClick={() => setBucket(b.key)}
              className="btn"
              style={{
                background: bucket === b.key ? b.bg : "transparent",
                color: bucket === b.key ? b.color : "var(--text-tertiary)",
                border: `0.5px solid ${bucket === b.key ? b.color : "var(--border)"}`,
                borderRadius: 999, padding: "5px 14px", fontSize: 11,
                display: "inline-flex", alignItems: "center", gap: 6,
              }}>
              {b.label}
              <span style={{ background: bucket === b.key ? "rgba(255,255,255,0.4)" : "var(--surface)", color: bucket === b.key ? b.color : "var(--text-tertiary)", borderRadius: 999, padding: "1px 6px", fontSize: 10, fontWeight: 600 }}>{b.count}</span>
            </button>
          ))}
          {/* Iter 167.f (Danny Customers spec Part 6) — Onboarding Specialist
              focus toggle. When ON, only shows customers assigned to me
              as the specialist. Auto-enabled for onboarding_specialist
              role; togglable by everyone. */}
          <button
            data-testid="my-implementations-toggle"
            onClick={() => setMyImplOnly(v => !v)}
            className="btn"
            style={{
              marginLeft: "auto",
              background: myImplOnly ? "var(--blue-light)" : "transparent",
              color: myImplOnly ? "var(--blue)" : "var(--text-tertiary)",
              border: `0.5px solid ${myImplOnly ? "var(--blue)" : "var(--border)"}`,
              borderRadius: 999, padding: "5px 14px", fontSize: 11,
            }}>
            {myImplOnly ? "✓ My implementations" : "My implementations"}
          </button>
        </div>

        {/* Contact Table */}
        {loading ? (
          <div className="card" style={{ padding: 40, textAlign: "center" }}><div className="skeleton" style={{ height: 200 }} /></div>
        ) : bucketFiltered.length === 0 ? (
          <div className="card" style={{ padding: 40, textAlign: "center" }}>
            <Users style={{ width: 32, height: 32, color: "var(--text-tertiary)", margin: "0 auto 8px" }} />
            <p style={{ fontSize: 13, color: "var(--text-secondary)" }}>No customers found</p>
          </div>
        ) : (
          <div className="card" style={{ overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "0.5px solid var(--border)", background: "var(--gray-light)" }}>
                  {(isLiveClientTab
                    ? ["Name", "Company", "Email", "Current Products", "Upsell Opportunities", ""]
                    : ["Name", "Company", "Email", "Phone", "Status", "Source", ""]
                  ).map(h => (
                    <th key={h} style={{ padding: "9px 12px", textAlign: "left", fontSize: 10, fontWeight: 600, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: "0.3px" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pagedFinal.map(c => {
                  const currentProducts = isLiveClientTab ? getContactProducts(c) : [];
                  const missing = isLiveClientTab ? getMissingProducts(c) : [];
                  return (
                    <tr key={c.id} style={{ borderBottom: "0.5px solid var(--border)", cursor: "pointer" }}
                      onClick={() => openPanel(c)}
                      onMouseOver={e => e.currentTarget.style.background = "var(--gray-light)"}
                      onMouseOut={e => e.currentTarget.style.background = "transparent"}
                      data-testid={`contact-row-${c.id}`}>
                      <td style={{ padding: "10px 12px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div style={{ width: 30, height: 30, borderRadius: "50%", background: isLiveClientTab ? "var(--green-light)" : "var(--blue-light)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 650, color: isLiveClientTab ? "var(--green)" : "var(--blue)" }}>
                            {(c.first_name?.[0] || "?")}{(c.last_name?.[0] || "")}
                          </div>
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 600 }}>{c.first_name} {c.last_name}</div>
                            <div style={{ fontSize: 10, color: "var(--text-tertiary)" }}>{c.job_title || ""}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: "10px 12px", fontSize: 12, color: "var(--text-secondary)" }}>{c.company || "--"}</td>
                      <td style={{ padding: "10px 12px", fontSize: 12, color: "var(--text-secondary)" }}>{c.email || "--"}</td>

                      {isLiveClientTab ? (
                        <>
                          <td style={{ padding: "10px 12px" }}>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                              {currentProducts.length > 0 ? currentProducts.slice(0, 2).map((p, i) => (
                                <span key={i} style={{ fontSize: 9, fontWeight: 600, padding: "1px 5px", borderRadius: 3, background: "var(--green-light)", color: "var(--green)" }}>{p}</span>
                              )) : <span style={{ fontSize: 10, color: "var(--text-tertiary)" }}>--</span>}
                              {currentProducts.length > 2 && <span style={{ fontSize: 9, color: "var(--text-tertiary)" }}>+{currentProducts.length - 2}</span>}
                            </div>
                          </td>
                          <td style={{ padding: "10px 12px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                              <TrendingUp style={{ width: 11, height: 11, color: "var(--blue)" }} />
                              <span style={{ fontSize: 11, color: "var(--blue)", fontWeight: 520 }}>{missing.length} products</span>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td style={{ padding: "10px 12px", fontSize: 12, color: "var(--text-secondary)" }}>{c.phone || "--"}</td>
                          <td style={{ padding: "10px 12px" }}>
                            <span className={`tag ${c.status === "live_client" ? "tag-green" : c.status === "referral" ? "tag-purple" : "tag-blue"}`} style={{ fontSize: 10 }}>
                              {c.status === "live_client" ? "Live client" : c.status === "referral" ? "Referral" : "Active"}
                            </span>
                          </td>
                          <td style={{ padding: "10px 12px", fontSize: 11, color: "var(--text-tertiary)" }}>{c.source || "--"}</td>
                        </>
                      )}

                      <td style={{ padding: "10px 12px" }}>
                        <ArrowRight style={{ width: 12, height: 12, color: "var(--text-tertiary)" }} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {bucketFiltered.length > PAGE_SIZE && (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12, padding: "0 4px" }} data-testid="contacts-pagination">
            <span style={{ fontSize: 11, color: "var(--text-tertiary)" }}>
              Showing {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, bucketFiltered.length)} of {bucketFiltered.length}
            </span>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <button className="btn" disabled={page <= 1} onClick={() => setPage(page - 1)}
                style={{ fontSize: 11, padding: "4px 8px", opacity: page <= 1 ? 0.4 : 1 }} data-testid="prev-page">
                <ChevronLeft style={{ width: 13, height: 13 }} />
              </button>
              <span style={{ fontSize: 12, fontWeight: 560, padding: "0 8px" }}>Page {page} of {bucketTotalPages}</span>
              <button className="btn" disabled={page >= bucketTotalPages} onClick={() => setPage(page + 1)}
                style={{ fontSize: 11, padding: "4px 8px", opacity: page >= bucketTotalPages ? 0.4 : 1 }} data-testid="next-page">
                <ChevronRight style={{ width: 13, height: 13 }} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Iter 167.g — Contact Detail slide-out panel removed. Clicking a
          customer now navigates to the full Customer Focus page at
          /customers/{id}. handleContactUpdate stays as a no-op reference
          for any future inline edits. */}

      {/* Contact Filter Panel */}
      {showFilters && (
        <ContactFilterPanel filters={cFilters} onChange={setCFilters} onClose={() => setShowFilters(false)} />
      )}
      {/* Shared Lead Filter Panel — same sidebar as /leads, state shared via localStorage */}
      {showLeadFilters && (
        <LeadFilterPanel
          filters={leadFilters}
          onChange={setLeadFilters}
          extra={leadExtra}
          onChangeExtra={setLeadExtra}
          onClose={() => setShowLeadFilters(false)}
        />
      )}
      {csvOpen && <CSVImportModal onClose={() => { setCsvOpen(false); fetchData(); }} type="contacts" />}
    </div>
  );
}
